
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import PledgeForm from '../components/PledgeForm';
import Timeline from '../components/Timeline';
import InteractiveStats from '../components/InteractiveStats';

const TakeActionPage = () => {
  const [showPledgeForm, setShowPledgeForm] = useState(false);

  const impactData = {
    homeActions: [
      {
        title: "Lower Your Thermostat",
        impact: "2,000 lbs CO₂ saved annually",
        description: "Lowering your thermostat by just 2°F in winter can make a huge difference",
        tips: "Use a programmable thermostat, add insulation, seal drafts"
      },
      {
        title: "LED Lighting",
        impact: "1,000 lbs CO₂ saved annually",
        description: "Switching to LED bulbs reduces energy consumption by up to 90%",
        tips: "Replace most used bulbs first, look for ENERGY STAR certification"
      },
      {
        title: "Smart Power Strips",
        impact: "500 lbs CO₂ saved annually",
        description: "Eliminate phantom power draw from electronics",
        tips: "Use for entertainment centers and home office setups"
      }
    ],
    transportation: [
      {
        title: "Carpool to Slopes",
        impact: "75% emissions reduction per person",
        description: "Share rides to reduce individual carbon footprints",
        tips: "Use ski club forums, coordinate with friends, share gear transport"
      },
      {
        title: "Vehicle Maintenance",
        impact: "20% fuel efficiency improvement",
        description: "Regular maintenance helps reduce emissions",
        tips: "Check tire pressure, remove roof racks when not in use"
      }
    ],
    equipment: [
      {
        title: "Buy Used Gear",
        impact: "Reduces manufacturing emissions by 100%",
        description: "Second-hand equipment prevents waste",
        tips: "Check local swap meets, online marketplaces"
      },
      {
        title: "Proper Maintenance",
        impact: "Doubles equipment lifespan",
        description: "Well-maintained gear lasts longer",
        tips: "Regular tuning, proper storage, immediate repairs"
      }
    ]
  };

  const climateStats = {
    current: {
      snowDepth: "20% decrease since 1970",
      seasonLength: "30 days shorter since 1970",
      snowLine: "600ft higher elevation"
    },
    projected2050: {
      snowDepth: "Additional 25% decrease",
      seasonLength: "2 months shorter season",
      resortRisk: "50% of resorts at risk"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover opacity-60"
          >
            <source src="https://player.vimeo.com/external/368763065.hd.mp4?s=fc6438e8c2b07c6127e09c4c73105c96cd61f337&profile_id=175" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/50 to-black/80" />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6"
          >
            The Time to Act is Now
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-blue-200 mb-8"
          >
            Our winters are changing rapidly. Learn how you can help protect them for future generations.
          </motion.p>
        </div>
      </section>

      {/* Climate Impact Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-white text-center mb-16"
          >
            The Impact on Our Winters
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Current Impact */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Current Changes</h3>
              <div className="space-y-6">
                {Object.entries(climateStats.current).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center text-2xl">
                      {key.includes('snow') ? '❄️' : '📅'}
                    </div>
                    <div>
                      <div className="text-xl font-bold text-white">{value}</div>
                      <div className="text-blue-200">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 2050 Projections */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6">2050 Projections</h3>
              <div className="space-y-6">
                {Object.entries(climateStats.projected2050).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center text-2xl">
                      {key.includes('snow') ? '❄️' : key.includes('resort') ? '🏔️' : '📅'}
                    </div>
                    <div>
                      <div className="text-xl font-bold text-white">{value}</div>
                      <div className="text-blue-200">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline Component */}
      <Timeline />

      {/* Interactive Stats Component */}
      <InteractiveStats />

      {/* Action Steps Section */}
      <section className="py-24 bg-blue-900/20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-white text-center mb-16"
          >
            How You Can Make a Difference
          </motion.h2>

          {/* Home Actions */}
          <div className="space-y-24">
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">At Home</h3>
              <div className="grid md:grid-cols-3 gap-8">
                {impactData.homeActions.map((action, index) => (
                  <motion.div
                    key={action.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  >
                    <h4 className="text-xl font-bold text-white mb-2">{action.title}</h4>
                    <div className="text-green-400 font-medium mb-4">{action.impact}</div>
                    <p className="text-blue-200 mb-4">{action.description}</p>
                    <div className="text-sm text-blue-300">
                      <strong>Tips:</strong> {action.tips}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Transportation */}
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">Transportation</h3>
              <div className="grid md:grid-cols-2 gap-8">
                {impactData.transportation.map((action, index) => (
                  <motion.div
                    key={action.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  >
                    <h4 className="text-xl font-bold text-white mb-2">{action.title}</h4>
                    <div className="text-green-400 font-medium mb-4">{action.impact}</div>
                    <p className="text-blue-200 mb-4">{action.description}</p>
                    <div className="text-sm text-blue-300">
                      <strong>Tips:</strong> {action.tips}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Equipment */}
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">Equipment & Gear</h3>
              <div className="grid md:grid-cols-2 gap-8">
                {impactData.equipment.map((action, index) => (
                  <motion.div
                    key={action.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  >
                    <h4 className="text-xl font-bold text-white mb-2">{action.title}</h4>
                    <div className="text-green-400 font-medium mb-4">{action.impact}</div>
                    <p className="text-blue-200 mb-4">{action.description}</p>
                    <div className="text-sm text-blue-300">
                      <strong>Tips:</strong> {action.tips}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-white mb-8"
          >
            Join the Movement
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xl text-blue-200 mb-12"
          >
            Take the Powder Promise and become part of the solution.
          </motion.p>

          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setShowPledgeForm(true)}
          >
            Take the Promise
          </Button>
        </div>
      </section>

      {/* Pledge Form Modal */}
      {showPledgeForm && (
        <PledgeForm onClose={() => setShowPledgeForm(false)} />
      )}
    </div>
  );
};

export default TakeActionPage;
